import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";

const SITE = "forfada.com";
const DESCRIPTION =
  "Diretório nacional de acompanhantes autônomos: fotógrafos, professores, terapeutas, designers, músicos, técnicos e muito mais. Encontre o acompanhante certo na sua região.";
const PRIVATE_PATH_RE = /^\/(admin|dashboard|publicar|conta|login|registrar|sucesso)(\/|$)/;
const ASSET_PATH_RE = /\.(ico|png|jpg|jpeg|svg|webp|gif|json|txt|xml|css|js|map)$/i;

interface SeoSettings {
  site_name?: string;
  default_title_suffix?: string;
  homepage_title?: string;
  homepage_description?: string;
  default_description?: string;
  default_canonical_base_url?: string;
  default_og_image?: string;
  default_og_type?: string;
  default_twitter_card?: string;
  default_twitter_site?: string;
  default_twitter_creator?: string;
  robots_index?: boolean;
  robots_follow?: boolean;
  route_templates?: RouteTemplate[];
  organization_name?: string;
  organization_logo?: string;
  organization_same_as?: string[];
  twitter_site?: string;
  twitter_creator?: string;
  theme_color?: string;
  google_site_verification?: string;
  bing_site_verification?: string;
}

interface RouteTemplate {
  route_pattern?: string;
  title?: string;
  description?: string;
  canonical_path?: string;
  og_image?: string;
  og_title?: string;
  og_description?: string;
  noindex?: boolean;
  nofollow?: boolean;
  robots?: string;
}

interface Props {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  jsonLd?: Record<string, unknown>;
}

let seoSettingsCache: SeoSettings | null = null;
let seoSettingsPromise: Promise<SeoSettings> | null = null;

async function loadSeoSettings(): Promise<SeoSettings> {
  if (seoSettingsCache) return seoSettingsCache;
  if (!seoSettingsPromise) {
    const raw = await api.get("/seo/settings");
    seoSettingsPromise = Promise.resolve(raw.data || {}).then((d) => d || {} as SeoSettings);
  }
  return seoSettingsPromise.then((d) => d || {} as SeoSettings);
}

function patternToRegex(pattern: string): { regex: RegExp; keys: string[] } {
  const keys: string[] = [];
  const source = `^${(pattern || "")
    .replace(/\/+$/, "")
    .replace(/:[A-Za-z0-9_]+/g, (m) => {
      keys.push(m.slice(1));
      return "([^/]+)";
    }) || "/"}$`;
  return { regex: new RegExp(source), keys };
}

function resolveTemplate(template: string, params: Record<string, string>): string {
  return (template || "").replace(/\{\{([A-Za-z0-9_]+)\}\}/g, (_, key) => params[key] || "");
}

function resolvePathPattern(pattern: string, params: Record<string, string>): string {
  return (pattern || "").replace(/:([A-Za-z0-9_]+)/g, (_, key) => params[key] || "");
}

function pickRouteTemplate(routeTemplates: RouteTemplate[] | undefined, routePath: string): { item: RouteTemplate; params: Record<string, string>; score: number } | null {
  const safePath = (routePath || "/").replace(/\/+$/, "") || "/";
  const candidates = Array.isArray(routeTemplates) ? routeTemplates : [];
  let best: { item: RouteTemplate; params: Record<string, string>; score: number } | null = null;
  for (const item of candidates) {
    const pattern = item?.route_pattern || "";
    if (!pattern) continue;
    const { regex, keys } = patternToRegex(pattern);
    const match = safePath.match(regex);
    if (!match) continue;
    const params: Record<string, string> = {};
    keys.forEach((k, idx) => {
      params[k] = decodeURIComponent(match[idx + 1] || "");
    });
    const score = pattern.split("/").filter(Boolean).length;
    if (!best || score > best.score) best = { item, params, score };
  }
  return best;
}

function absoluteUrl(value: string, base: string): string {
  const text = String(value || "").trim();
  if (!text) return "";
  if (/^https?:\/\//i.test(text)) return text;
  if (!base) return text;
  return `${base}${text.startsWith("/") ? text : `/${text}`}`;
}

export default function SEO({ title, description, path = "", image, jsonLd }: Props): React.JSX.Element {
  const [cfg, setCfg] = useState<SeoSettings>(() => seoSettingsCache || {});

  useEffect(() => {
    if (seoSettingsCache) return undefined;
    let active = true;
    loadSeoSettings().then((data) => {
      if (active) setCfg(data || {});
    });
    return () => {
      active = false;
    };
  }, []);

  const siteName = cfg.site_name || SITE;
  const titleSuffix = cfg.default_title_suffix || siteName;
  const homepageTitle = cfg.homepage_title || "";
  const homepageDescription = cfg.homepage_description || "";
  const isHome = !path || path === "/";
  const routeMatch = pickRouteTemplate(cfg.route_templates, path || "/");
  const routeTitle = routeMatch ? resolveTemplate(routeMatch.item?.title || "", routeMatch.params) : "";
  const routeDescription = routeMatch ? resolveTemplate(routeMatch.item?.description || "", routeMatch.params) : "";
  const routeCanonicalPath = routeMatch
    ? resolvePathPattern(resolveTemplate(routeMatch.item?.canonical_path || "", routeMatch.params), routeMatch.params)
    : "";
  const routeOgImage = routeMatch ? resolveTemplate(routeMatch.item?.og_image || "", routeMatch.params) : "";
  const routeOgTitle = routeMatch ? resolveTemplate(routeMatch.item?.og_title || routeMatch.item?.title || "", routeMatch.params) : "";
  const routeOgDescription = routeMatch ? resolveTemplate(routeMatch.item?.og_description || routeMatch.item?.description || "", routeMatch.params) : "";
  const routeRobots = routeMatch?.item?.robots || "";

  const resolvedTitle = title || routeTitle || (isHome ? homepageTitle : "");
  const fullTitle = resolvedTitle ? `${resolvedTitle} · ${titleSuffix}` : `${siteName} — uma forfada perto de você`;
  const desc =
    description || routeDescription || (isHome ? homepageDescription : "") || cfg.default_description || DESCRIPTION;
  const base = cfg.default_canonical_base_url || (typeof window !== "undefined" ? window.location.origin : "");
  const safeBase = `${base}`.replace(/\/+$/, "");
  const pathWithTemplate = routeCanonicalPath || path || "";
  const safePath = `${pathWithTemplate}`.startsWith("/") ? pathWithTemplate : `/${pathWithTemplate}`;
  const url = safeBase ? `${safeBase}${safePath === "/" ? "" : safePath}` : "";
  const ogImage = image || routeOgImage || cfg.default_og_image || "";
  const ogImageUrl = absoluteUrl(ogImage, safeBase);
  const forceNoindex = PRIVATE_PATH_RE.test(safePath) || ASSET_PATH_RE.test(safePath);
  const robotsIndex = forceNoindex ? "noindex" : routeRobots.includes("noindex") || cfg.robots_index === false ? "noindex" : "index";
  const robotsFollow = routeRobots.includes("nofollow") || cfg.robots_follow === false ? "nofollow" : "follow";
  const robots = `${robotsIndex},${robotsFollow}`;
  const isListing = path && (path.startsWith("/perfil/") || path.startsWith("/acompanhante/"));
  const ogType = isListing ? "profile" : (cfg.default_og_type || "website");
  const pageJsonLd = useMemo(() => {
    if (!jsonLd) return null;
    return JSON.stringify(jsonLd);
  }, [jsonLd]);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {url && <link rel="canonical" href={url} />}
      <meta name="robots" content={robots} />

      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={routeOgTitle || fullTitle} />
      <meta property="og:description" content={routeOgDescription || desc} />
      {url && <meta property="og:url" content={url} />}
      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content="pt_BR" />
      {ogImageUrl && <meta property="og:image" content={ogImageUrl} />}
      {ogImageUrl && <meta property="og:image:alt" content={fullTitle} />}

      <meta name="twitter:card" content={cfg.default_twitter_card || "summary_large_image"} />
      <meta name="twitter:title" content={routeOgTitle || fullTitle} />
      <meta name="twitter:description" content={routeOgDescription || desc} />
      {(cfg.default_twitter_site || cfg.twitter_site) && <meta name="twitter:site" content={cfg.default_twitter_site || cfg.twitter_site} />}
      {(cfg.default_twitter_creator || cfg.twitter_creator) && <meta name="twitter:creator" content={cfg.default_twitter_creator || cfg.twitter_creator} />}
      {ogImageUrl && <meta name="twitter:image" content={ogImageUrl} />}

      {cfg.google_site_verification && (
        <meta name="google-site-verification" content={cfg.google_site_verification} />
      )}
      {cfg.bing_site_verification && (
        <meta name="msvalidate.01" content={cfg.bing_site_verification} />
      )}

      <meta name="theme-color" content={cfg.theme_color || "#fff7ed"} />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={siteName} />

      
      {pageJsonLd && <script type="application/ld+json">{pageJsonLd}</script>}
      
      <html lang="pt-BR" />
    </Helmet>
  );
}